import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { addNotification } from 'notifications';
import { resetClientAuthState } from 'api/resetClientAuthState';
import { setRole, setUserRoleIds } from 'api/tokenStorage';
import { api } from 'api/v1';
import { applyAuthSession } from 'hooks/useAuthSession';
import { getApiErrorMessage } from 'utils/getApiErrorMessage';
import { parseEmailNotVerifiedError, parsePasswordNotSetError } from 'utils/emailNotVerified';
import { resolvePostLoginNavigation } from 'utils/postLoginNavigation';
import { captureReturnTo, consumeReturnTo } from 'utils/returnTo';

interface LoginVariables {
  email?: string;
  phone?: string;
  password: string;
}

const useLoginMutation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  return useMutation<any, Error, LoginVariables>({
    mutationFn: (variables) =>
      api
        .post(
          '/login/',
          variables.phone
            ? { phone: variables.phone, password: variables.password }
            : { email: variables.email, password: variables.password },
          { skipAuthRefresh: true, skipGlobalErrorHandler: true },
        )
        .then((res) => res.data),
    onSuccess: (data) => {
      captureReturnTo(
        searchParams,
        (location.state as { from?: string } | null)?.from,
      );
      const returnTo = consumeReturnTo();
      const { path, preferredRoleId } = resolvePostLoginNavigation(
        data.roles ?? [],
        returnTo,
      );

      // Drop previous account caches before writing the new session.
      resetClientAuthState(queryClient);

      setRole(data.roles ?? [], preferredRoleId);
      setUserRoleIds(data.roles ?? []);
      applyAuthSession(data);

      addNotification('Вход выполнен', 'success');

      navigate(path, { replace: true });
    },
    onError: (error) => {
      if (parseEmailNotVerifiedError(error) || parsePasswordNotSetError(error)) {
        return;
      }
      addNotification(getApiErrorMessage(error, 'Ошибка входа'), 'error');
    },
  });
};

export { useLoginMutation };
